/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  supplier: 'ALL',
};

const initPhoto = {
  id: '',
  name: '',
};

function RpProductPrice() {
  const mod = 'rp_product_price';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState(initPhoto);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        view(find);
        setUrlExcel(result.excel);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1} onClick={excel}>
              <MDButton variant='gradient' color='success'>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const enableDisplay = (inp) => {
    if (inp === 'Y') return <Icon color='info'>verified</Icon>;
    return <Icon color='error'>not_interested</Icon>;
  };

  const profit = (item) => {
    const price = item.price * 1;
    const cost = item.cost_fix * 1;
    const cost_markup = item.cost_markup * 1;
    const cost_sell = cost + (cost * cost_markup) / 100;
    const profit = cost_sell === 0 ? 100 : (price / cost_sell) * 100 - 100;

    return profit;
  };

  const costSell = (item) => {
    const cost = item.cost_fix * 1;
    const cost_markup = item.cost_markup * 1;
    const cost_sell = cost + (cost * cost_markup) / 100;

    return cost_sell;
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_product'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>รูป</TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell width={100} align='right' className='nowrap'>
                      ปันทุน
                    </TableCell>
                    <TableCell width={100} align='right' className='nowrap'>
                      Mark up
                    </TableCell>
                    <TableCell width={100} align='right' className='nowrap'>
                      ทุนขาย
                    </TableCell>
                    <TableCell width={100} align='right' className='nowrap'>
                      ราคาขาย
                    </TableCell>
                    <TableCell width={100} align='right' className='nowrap'>
                      ผลกำไร
                    </TableCell>
                    <TableCell width={100} align='right' className='highlight-success nowrap'>
                      ซื้อล่าสุด
                    </TableCell>
                    <TableCell width={100} align='right' className='highlight-warning nowrap'>
                      รับล่าสุด
                    </TableCell>
                    <TableCell width={100} align='right' className='highlight-danger nowrap'>
                      คงเหลือ
                    </TableCell>
                    <TableCell width={90} className='nowrap'>
                      หน่วยหลัก
                    </TableCell>
                    <TableCell width={70} className='nowrap'>
                      เปิด/ปิด
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell
                        onClick={() => {
                          setPhoto(row);
                          setModalPhoto(true);
                        }}
                      >
                        {Func.imageDisplay(row)}
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>
                          {row.code} <span className='color-default'>{row.sku}</span>
                        </MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.cost_fix)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.cost_markup)}</MDBox>
                        <MDBox>%</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(costSell(row), 2)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.price)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(profit(row))}</MDBox>
                        <MDBox>%</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-success'>
                        <MDBox>{Func.numberDisplay(row.price_purchase)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-warning'>
                        <MDBox>{Func.numberDisplay(row.price_receive)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-danger'>
                        <MDBox>{Func.numberDisplay(row.quantity_balance)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{enableDisplay(row.product_main)}</TableCell>
                      <TableCell align='center'>{enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt={photo.name}
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_product`), t(`menu.rp_product_price`)]} />
      {renderList()}
      {renderPhoto()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductPrice;
